// src/shared/api-client/endpoints/suggestionBox.js
// Suggestion Box API endpoints

import { baseApi } from "../baseApi";

const suggestionBoxApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ========================================================================
    // TOPIC ENDPOINTS
    // ========================================================================

    getSuggestionTopics: builder.query({
      query: () => "/api/suggestion-box/topics",
      providesTags: (result) =>
        result?.data?.topics
          ? [
              ...result.data.topics.map(({ id }) => ({
                type: "SuggestionTopic",
                id,
              })),
              { type: "SuggestionTopic", id: "LIST" },
            ]
          : [{ type: "SuggestionTopic", id: "LIST" }],
    }),

    createSuggestionTopic: builder.mutation({
      query: (body) => ({
        url: "/api/suggestion-box/topics",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "SuggestionTopic", id: "LIST" }],
    }),

    updateSuggestionTopic: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/suggestion-box/topics/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SuggestionTopic", id },
        { type: "SuggestionTopic", id: "LIST" },
      ],
    }),

    deleteSuggestionTopic: builder.mutation({
      query: (id) => ({
        url: `/api/suggestion-box/topics/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "SuggestionTopic", id: "LIST" }],
    }),

    // ========================================================================
    // SUGGESTION ENDPOINTS
    // ========================================================================

    // Admin-only: list all suggestions (paginated, filterable by topicId)
    getSuggestions: builder.query({
      query: (params = {}) => ({
        url: "/api/suggestion-box",
        params,
      }),
      providesTags: (result) =>
        result?.data?.suggestions
          ? [
              ...result.data.suggestions.map(({ id }) => ({
                type: "Suggestion",
                id,
              })),
              { type: "Suggestion", id: "LIST" },
            ]
          : [{ type: "Suggestion", id: "LIST" }],
    }),

    // Member: submit a suggestion
    submitSuggestion: builder.mutation({
      query: (body) => ({
        url: "/api/suggestion-box",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Suggestion", id: "LIST" }],
    }),

    // Admin: soft-delete a suggestion
    deleteSuggestion: builder.mutation({
      query: (id) => ({
        url: `/api/suggestion-box/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Suggestion", id: "LIST" }],
    }),

    // ========================================================================
    // REPLY ENDPOINTS
    // ========================================================================

    createSuggestionReply: builder.mutation({
      query: ({ id, content }) => ({
        url: `/api/suggestion-box/${id}/reply`,
        method: "POST",
        body: { content },
      }),
      invalidatesTags: [{ type: "Suggestion", id: "LIST" }],
    }),

    updateSuggestionReply: builder.mutation({
      query: ({ id, content }) => ({
        url: `/api/suggestion-box/${id}/reply`,
        method: "PUT",
        body: { content },
      }),
      invalidatesTags: [{ type: "Suggestion", id: "LIST" }],
    }),

    deleteSuggestionReply: builder.mutation({
      query: (id) => ({
        url: `/api/suggestion-box/${id}/reply`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Suggestion", id: "LIST" }],
    }),
  }),
});

export const {
  useGetSuggestionTopicsQuery,
  useCreateSuggestionTopicMutation,
  useUpdateSuggestionTopicMutation,
  useDeleteSuggestionTopicMutation,
  useGetSuggestionsQuery,
  useSubmitSuggestionMutation,
  useDeleteSuggestionMutation,
  useCreateSuggestionReplyMutation,
  useUpdateSuggestionReplyMutation,
  useDeleteSuggestionReplyMutation,
} = suggestionBoxApi;
